import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";

type WpFetchResult<T> = {
  data: T;
  loading: boolean;
  error: Error | null;
};

/**
 * Generic hook for fetching WordPress API data.
 * Pass any service function and optional deps.
 *
 * @example
 * const { data, loading } = useWpFetch(() => getNews(), []);
 * const { data, loading } = useWpFetch(() => id ? getDogById(id) : null, [id]);
 */
export function useWpFetch<T>(
  fetcher: () => Promise<AxiosResponse<T>> | null,
  deps: React.DependencyList,
  initialData: T,
): WpFetchResult<T> {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const promise = fetcher();
    if (!promise) return;

    setLoading(true);
    promise
      .then((response) => {
        setData(response.data);
        setError(null);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error };
}
